import { useCallback } from 'react';

import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import { OwnerInformation } from '@/legacy/components/user/onboardingWizard/company/ownershipInformation/model/OwnerInformation';
import {
  CompanyOutstandingVerificationItemsViewModel,
  OnboardingWizardStatusViewModel,
  Status,
  VerificationStatus,
  VerificationStatusIdentityViewModel,
} from '@/legacy/services/verification/verification';

import { VerificationProgress } from '../types';
import {
  isKybSubmittingUser as isKybSubmittingUserObservable,
  isKycStep as isKycStepObservable,
  isUbo as isUboObservable,
} from '../utils/availability';
import {
  allVerificationResults as allResults,
  bankAccountVerificationCompleted as bankAccountVerificationCompletedObservable,
  bankAccountVerificationInProgress as bankAccountVerificationInProgressObservable,
  bankAccountVerificationNotStarted as bankAccountVerificationNotStartedObservable,
  bankAccountVerificationProgress as bankAccountVerificationProgressObservable,
  companyVerificationStatus as companyVerificationStatusObservable,
  testPaymentMade as testPaymentMadeObservable,
  userVerificationProgress as userVerificationProgressObservable,
} from '../verification';

export type VerificationActions = {
  setCompanyOnboardingStatus: (status: Status) => void;
  setIsKybSubmittingUser: (isSubmitting: boolean) => void;
  setUserVerificationSubmitted: (isSubmitted: boolean) => void;
};

export type VerificationState = {
  additionalVerificationItems: CompanyOutstandingVerificationItemsViewModel;
  bankAccountVerificationCompleted: boolean;
  bankAccountVerificationInProgress: boolean;
  bankAccountVerificationNotStarted: boolean;
  bankAccountVerificationProgress: VerificationProgress;
  companyVerification: VerificationStatus;
  companyVerificationCompleted: boolean;
  companyVerificationInProgress: boolean;
  companyVerificationNotStarted: boolean;
  companyVerificationProgress: VerificationProgress;
  /**
   * Raw backend status for company verification. Please refer to @see {@link Status} for more
   * information.
   */
  companyVerificationStatus: Status;
  error: unknown;
  isKybSubmittingUser: boolean;
  isKycStep: boolean;
  isLoading: boolean;
  isUbo: boolean;
  ownershipInformation: Array<OwnerInformation>;
  testPaymentMade: boolean;
  userVerification: VerificationStatusIdentityViewModel;
  userVerificationCompleted: boolean;
  userVerificationInProgress: boolean;
  userVerificationNotStarted: boolean;
  userVerificationProgress: VerificationProgress;
  /**
   * Raw backend status for user verification. Please refer to @see {@link Status} for more
   * information.
   */
  userVerificationStatus: Status;
  verification: OnboardingWizardStatusViewModel;
  verificationCompleted: boolean;
  verificationFailed: boolean;
  verificationInProgress: boolean;
};

export function useVerification(): [VerificationState, VerificationActions] {
  const bankAccountVerificationCompleted = useSyncKoObservable(
    bankAccountVerificationCompletedObservable
  );
  const bankAccountVerificationInProgress = useSyncKoObservable(
    bankAccountVerificationInProgressObservable
  );
  const bankAccountVerificationNotStarted = useSyncKoObservable(
    bankAccountVerificationNotStartedObservable
  );
  const bankAccountVerificationProgress = useSyncKoObservable(
    bankAccountVerificationProgressObservable
  );
  const testPaymentMade = useSyncKoObservable(testPaymentMadeObservable);
  const {
    data: {
      additionalVerificationItems,
      companyVerification,
      companyVerificationCompleted,
      companyVerificationInProgress,
      companyVerificationNotStarted,
      companyVerificationProgress,
      companyVerificationStatus,
      ownershipInformation,
      userVerification,
      userVerificationCompleted,
      userVerificationInProgress,
      userVerificationNotStarted,
      userVerificationProgress,
      userVerificationStatus,
      verification,
      verificationCompleted,
      verificationFailed,
      verificationInProgress,
    },
    error,
    isLoading,
  } = useSyncKoObservable(allResults);
  const {
    data: isKybSubmittingUser,
    error: isKybSubmittingUserError,
    isLoading: isKybSubmittingUserLoading,
  } = useSyncKoObservable(isKybSubmittingUserObservable);
  const {
    data: isUbo,
    error: isUboError,
    isLoading: isUboLoading,
  } = useSyncKoObservable(isUboObservable);
  const {
    data: isKycStep,
    error: isKycStepError,
    isLoading: isKycStepLoading,
  } = useSyncKoObservable(isKycStepObservable);
  const setCompanyOnboardingStatus = useCallback((status: Status) => {
    companyVerificationStatusObservable({ data: status, error: null, isLoading: false });
  }, []);
  const setIsKybSubmittingUser = useCallback((isSubmitting: boolean) => {
    isKybSubmittingUserObservable({ data: isSubmitting, error: null, isLoading: false });
  }, []);
  const setUserVerificationSubmitted = useCallback((isSubmitted: boolean) => {
    userVerificationProgressObservable({
      data: isSubmitted ? 'in-progress' : 'not-started',
      error: null,
      isLoading: false,
    });
  }, []);
  return [
    {
      additionalVerificationItems,
      bankAccountVerificationCompleted,
      bankAccountVerificationInProgress,
      bankAccountVerificationNotStarted,
      bankAccountVerificationProgress,
      companyVerification,
      companyVerificationCompleted,
      companyVerificationInProgress,
      companyVerificationNotStarted,
      companyVerificationProgress,
      companyVerificationStatus,
      error: error || isKybSubmittingUserError || isKycStepError || isUboError,
      isKybSubmittingUser,
      isKycStep,
      isLoading: isLoading || isKybSubmittingUserLoading || isKycStepLoading || isUboLoading,
      isUbo,
      ownershipInformation,
      testPaymentMade,
      userVerification,
      userVerificationCompleted,
      userVerificationInProgress,
      userVerificationNotStarted,
      userVerificationProgress,
      userVerificationStatus,
      verification,
      verificationCompleted,
      verificationFailed,
      verificationInProgress,
    },
    {
      setCompanyOnboardingStatus,
      setIsKybSubmittingUser,
      setUserVerificationSubmitted,
    },
  ];
}
